/**
 * 受け取りの登録（/money/:mode/get-paid）。サーバー: GET/POST /v1/money/payee・POST …/onboarding-session・POST …/sync（money/routes.ts）。
 *
 * 旧方式（Payments 画面の /v1/projects/:id/payments/connect = Stripe Express のホスト型の登録へ飛ばす）を置き換える。
 * 新方式は Accounts v2 の口座を Kumo が作り、本人確認と口座の入力は**このページの中の埋め込み**で済ませる（stripeConnect.ts）。
 * クリエイターに見せるのは Kumo の言葉の状態（moneyLogic.payeeHeadline）だけで、Stripe の名前・口座 ID・要件の生の名前は出さない。
 *
 * 鍵の無いローカル（偽の Connect）では、埋め込みの代わりに「模擬の本人確認」を出す。偽の口座は登録フォームを開いた時点で有効になる
 * （money/connect.ts の autoActivate）ので、「完了」で状態を読み直せば受け取れる状態になる = 画面から出金まで通しで試せる。
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { ErrorState, PageHeader, Spinner } from '../../components/ui';
import { useResource } from '../../hooks';
import { errorText, getLocale, useT } from '../../i18n';
import type { OnboardingSession, PayeeView } from '../../moneyApi';
import { payeeHeadline, userActionCount, type PayeeHeadline } from '../../moneyLogic';
import { mountOnboarding } from '../../stripeConnect';
import { BankAccountNotice } from './BankAccountNotice';
import { useMoney } from './MoneyShell';

const TONE: Record<PayeeHeadline, 'ok' | 'warn' | 'info' | 'danger'> = {
  not_started: 'info',
  needs_info: 'warn',
  in_review: 'info',
  ready: 'ok',
  on_hold: 'danger',
  closed: 'danger'
};

type Form = { kind: 'closed' } | { kind: 'sim' } | { kind: 'embed' } | { kind: 'unavailable' };

export function PayeePage() {
  const { mode, livemode } = useMoney();
  const r = useResource<PayeeView>(`/v1/money/payee?livemode=${livemode}`);
  const { t, tr } = useT();
  const [country, setCountry] = useState<'US' | 'CA'>('US');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState<Form>({ kind: 'closed' });
  const [firstSession, setFirstSession] = useState<OnboardingSession | null>(null);

  // モードを切り替えたら開いていたフォーム・通知を閉じる（test の登録フォームが live の画面に残らないように）
  useEffect(() => {
    setForm({ kind: 'closed' });
    setNotice(null);
    setErr(null);
  }, [livemode]);

  const payee = r.data?.payee ?? null;
  const headline = r.data ? payeeHeadline(payee, r.data.eligibility) : null;
  const due = userActionCount(payee?.requirementsDue ?? null);

  const session = () => api<OnboardingSession>('/v1/money/payee/onboarding-session', { method: 'POST', body: { livemode } });

  const openForm = async () => {
    setBusy(true);
    setErr(null);
    setNotice(null);
    try {
      // POST /payee は「無ければ作る」（money/payees.ts の ensureConnectedAccount・べき等）。既にある受取人でも毎回呼ぶ:
      // 途中で失敗して口座の紐づけが無いまま残った受取人を、ここで作り直せる（フォームを開く前提 = 口座があること）
      await api('/v1/money/payee', { method: 'POST', body: { country: payee?.country ?? country, livemode } });
      if (!payee) {
        // 作った直後に一度だけ状態を取り込む（本人が入力すべき項目の一覧が入る = 「追加の情報が必要です」と正しく出る）。
        // フォームを開く（Account Session を作る）前に行う: 偽の Connect はフォームを開いた時点で有効になるので、後だと入力前に「受け取れる」になる
        await api('/v1/money/payee/sync', { method: 'POST', body: { livemode } });
      }
      const s = await session();
      if (s.provider === 'fake') setForm({ kind: 'sim' });
      else if (!s.publishableKey) setForm({ kind: 'unavailable' });
      else {
        setFirstSession(s);
        setForm({ kind: 'embed' });
      }
      if (!payee) r.reload();
    } catch (e) {
      setErr(errorText(e as ApiError));
    } finally {
      setBusy(false);
    }
  };

  const sync = async (message = true) => {
    setBusy(true);
    setErr(null);
    try {
      await api('/v1/money/payee/sync', { method: 'POST', body: { livemode } });
      if (message) setNotice(t('payee.checked'));
      r.reload();
    } catch (e) {
      setErr(errorText(e as ApiError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader eyebrow={t(`money.mode.${mode}`)} title={t('payee.title')} />
      <p className="page-lead muted">{t('payee.lead')}</p>
      {r.loading && !r.data && <Spinner />}
      {r.error && <ErrorState error={r.error} what={t('payee.title')} onRetry={r.reload} />}
      {notice && (
        <div className="alert alert-ok" role="status">
          {notice}
        </div>
      )}
      {headline && (
        <section className={`card status-card status-${TONE[headline]}`}>
          <div className="status-head">
            <span className={`status-dot status-dot-${TONE[headline]}`} aria-hidden="true" />
            <div>
              <h2 className="card-title">{t(`payee.headline.${headline}`)}</h2>
              <p className="small">{t(`payee.body.${headline}`)}</p>
            </div>
          </div>

          {payee && (
            <dl className="kv status-kv">
              <dt>{t('payee.country')}</dt>
              <dd>{t(`payee.country.${payee.country}`)}</dd>
              {due > 0 && (
                <>
                  <dt>{t('payee.status')}</dt>
                  <dd>{t('payee.dueCount', { count: due })}</dd>
                </>
              )}
              {payee.taxForm && (
                <>
                  <dt>{t('payee.tax')}</dt>
                  <dd>
                    {payee.taxForm === 'w9' ? t('payee.tax.w9') : payee.taxForm === 'w8ben' ? t('payee.tax.w8ben') : payee.taxForm}
                    {payee.taxStatus &&
                      ` · ${['missing', 'pending', 'verified', 'invalid'].includes(payee.taxStatus) ? t(`payee.taxStatus.${payee.taxStatus as 'missing'}`) : payee.taxStatus}`}
                  </dd>
                </>
              )}
            </dl>
          )}
          {payee?.requirementsDeadline === 'past_due' && (
            <p className="alert alert-warn" role="note">
              {t('payee.pastDue')}
            </p>
          )}
          {/* 振込先は銀行口座に（デビットカードではマイナス残高を引き落とせない = 返金の回収ができない）。BankAccountNotice の注記 */}
          {payee && headline !== 'on_hold' && <BankAccountNotice livemode={livemode} mode={mode} payee={payee} hideAction />}
          {mode === 'test' && <p className="muted small">{t('payee.testNote')}</p>}

          {err && (
            <p className="alert alert-error" role="alert">
              {err}
            </p>
          )}

          {headline === 'not_started' && (
            <div className="form-inline payee-start">
              <label className="field">
                <span>{t('payee.country')}</span>
                <select value={country} onChange={(e) => setCountry(e.target.value as 'US' | 'CA')}>
                  <option value="US">{t('payee.country.US')}</option>
                  <option value="CA">{t('payee.country.CA')}</option>
                </select>
                <small className="muted">{t('payee.countryHint')}</small>
              </label>
            </div>
          )}

          {form.kind === 'closed' && headline !== 'closed' && headline !== 'on_hold' && (
            <div className="form-actions">
              {payee && (
                <button type="button" className="btn btn-ghost" onClick={() => void sync()} disabled={busy}>
                  {t('payee.refresh')}
                </button>
              )}
              <button
                type="button"
                className={`btn ${headline === 'ready' || headline === 'in_review' ? 'btn-ghost' : 'btn-primary'}`}
                onClick={() => void openForm()}
                disabled={busy}
              >
                {busy
                  ? t('common.working')
                  : headline === 'not_started'
                    ? t('payee.start')
                    : headline === 'needs_info'
                      ? t('payee.continue')
                      : t('payee.update')}
              </button>
            </div>
          )}
          {headline === 'ready' && form.kind === 'closed' && (
            <p className="small">
              <Link to={`/money/${mode}/withdraw`}>{t('money.nav.withdraw')} →</Link>
            </p>
          )}
        </section>
      )}

      {form.kind === 'sim' && (
        <section className="card sim-card">
          <h2 className="card-title">{t('payee.sim.title')}</h2>
          <p className="small">{t('payee.sim.body')}</p>
          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setForm({ kind: 'closed' })}>
              {t('payee.embed.close')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={async () => {
                await sync(false);
                setForm({ kind: 'closed' });
              }}
            >
              {t('payee.sim.finish')}
            </button>
          </div>
        </section>
      )}

      {form.kind === 'unavailable' && (
        <div className="alert alert-warn" role="note">
          <strong>{t('payee.embed.unavailableTitle')}</strong>
          <span>{tr('payee.embed.unavailableBody', { keyVar: <code>{livemode ? 'STRIPE_LIVE_PUBLISHABLE_KEY' : 'STRIPE_TEST_PUBLISHABLE_KEY'}</code> })}</span>
        </div>
      )}

      {form.kind === 'embed' && firstSession?.publishableKey && (
        <EmbeddedOnboarding
          publishableKey={firstSession.publishableKey}
          first={firstSession.clientSecret}
          fetchSecret={async () => (await session()).clientSecret}
          onExit={() => {
            setForm({ kind: 'closed' });
            void sync(false);
          }}
        />
      )}
    </>
  );
}

/**
 * 埋め込みの本人確認のフォーム。client_secret は最初の 1 回だけ手元の値を使い、期限切れの再取得はサーバーに取りに行く
 * （Connect.js が fetchClientSecret を呼び直す = 30 分を超えて入力していても続けられる）
 */
function EmbeddedOnboarding({
  publishableKey,
  first,
  fetchSecret,
  onExit
}: {
  publishableKey: string;
  first: string;
  fetchSecret: () => Promise<string>;
  onExit: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const { t } = useT();
  const exitRef = useRef(onExit);
  exitRef.current = onExit;

  useEffect(() => {
    let cleanup: (() => void) | null = null;
    let used = false;
    let alive = true;
    const el = ref.current;
    if (!el) return;
    mountOnboarding(el, {
      publishableKey,
      locale: getLocale() === 'ja' ? 'ja-JP' : 'en-US',
      fetchClientSecret: async () => {
        if (!used) {
          used = true;
          return first;
        }
        return fetchSecret();
      },
      onExit: () => exitRef.current()
    })
      .then((c) => {
        if (!alive) return c();
        cleanup = c;
        setState('ready');
      })
      .catch(() => alive && setState('failed'));
    return () => {
      alive = false;
      cleanup?.();
    };
    // publishableKey / first が変わるのはフォームを開き直したときだけ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishableKey, first]);

  return (
    <section className="card embed-card">
      {state === 'loading' && <p className="muted small">{t('payee.embed.loading')}</p>}
      {state === 'failed' && (
        <p className="alert alert-error" role="alert">
          {t('payee.embed.failed')}
        </p>
      )}
      <div ref={ref} className="embed-host" />
      <div className="form-actions">
        <button type="button" className="btn btn-ghost" onClick={() => exitRef.current()}>
          {t('payee.embed.close')}
        </button>
      </div>
    </section>
  );
}
